import React, { useState } from 'react';

// Real DNS-over-HTTPS lookups via Google's public resolver — no backend
// needed, works straight from the browser. This genuinely checks live DNS,
// it doesn't just show a static checklist.
async function dnsLookup(name, type) {
  const res = await fetch(`https://dns.google/resolve?name=${encodeURIComponent(name)}&type=${type}`);
  if (!res.ok) throw new Error('DNS lookup request failed');
  return res.json();
}

/**
 * "Verify DNS Records" check for the Email integration — confirms SPF is
 * published for the sending domain, reports MX records, and lets the user
 * check one specific DKIM/CNAME record by name (since the exact record name
 * varies by provider — SendGrid, SES, etc. — and isn't guessable generically).
 */
export default function EmailDnsVerify({ domain }) {
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [customRecord, setCustomRecord] = useState('');

  const runCheck = async () => {
    if (!domain || !domain.trim()) {
      setResult({ error: 'Enter a sending domain above first, then verify.' });
      return;
    }
    setChecking(true);
    setResult(null);
    try {
      const [txt, mx] = await Promise.all([
        dnsLookup(domain.trim(), 'TXT'),
        dnsLookup(domain.trim(), 'MX'),
      ]);
      const txtRecords = (txt.Answer || []).map(a => (a.data || '').replace(/^"|"$/g, ''));
      const spf = txtRecords.find(r => r.includes('v=spf1')) || null;
      const mxRecords = (mx.Answer || []).map(a => a.data);

      let dkim = null;
      if (customRecord.trim()) {
        const [cname, dtxt] = await Promise.all([
          dnsLookup(customRecord.trim(), 'CNAME').catch(() => null),
          dnsLookup(customRecord.trim(), 'TXT').catch(() => null),
        ]);
        const found = [...(cname?.Answer || []), ...(dtxt?.Answer || [])];
        dkim = found.map(a => a.data);
      }

      setResult({ spf, txtRecords, mxRecords, dkim });
    } catch (e) {
      setResult({ error: e.message || 'Lookup failed — check the domain spelling and try again.' });
    } finally {
      setChecking(false);
    }
  };

  return (
    <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, padding: 20 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>🔍 Verify DNS Records</div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 14, lineHeight: 1.6 }}>
        Live DNS lookups for <strong>{domain || 'your domain'}</strong> — confirms SPF is published, and you can check a specific DKIM/CNAME record by name (the exact name depends on your provider, e.g. <code style={{ background: 'rgba(0,0,0,0.06)', padding: '1px 5px', borderRadius: 4 }}>s1._domainkey.{domain || 'yourdomain.com'}</code>).
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <input
          className="crm-input"
          placeholder="Optional: DKIM record to check, e.g. s1._domainkey.yourdomain.com"
          value={customRecord}
          onChange={e => setCustomRecord(e.target.value)}
          style={{ flex: 1, minWidth: 220 }}
        />
        <button className="btn-primary" onClick={runCheck} disabled={checking} style={{ flexShrink: 0 }}>
          {checking ? '⏳ Checking…' : '🔄 Verify DNS Records'}
        </button>
      </div>

      {result?.error && (
        <div style={{ fontSize: 12, color: '#dc2626', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '8px 12px' }}>
          {result.error}
        </div>
      )}

      {result && !result.error && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <DnsCheckRow ok={!!result.spf} label="SPF record" detail={result.spf || 'No "v=spf1" TXT record found yet — DNS changes can take a few hours to propagate.'} />
          <DnsCheckRow ok={result.mxRecords.length > 0} label="MX records" detail={result.mxRecords.length ? `${result.mxRecords.length} record(s) found: ${result.mxRecords.join(', ')}` : 'No MX records found for this domain.'} />
          {customRecord.trim() && (
            <DnsCheckRow
              ok={result.dkim && result.dkim.length > 0}
              label={`DKIM record (${customRecord.trim()})`}
              detail={result.dkim && result.dkim.length ? result.dkim.join(', ') : 'Not found yet — double-check the exact record name your provider gave you.'}
            />
          )}
          <div style={{ fontSize: 11, color: '#94a3b8' }}>Checked just now via Google's public DNS-over-HTTPS resolver (dns.google).</div>
        </div>
      )}
    </div>
  );
}

function DnsCheckRow({ ok, label, detail }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
      <span style={{ fontSize: 14, flexShrink: 0 }}>{ok ? '✅' : '⚠️'}</span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{label}</div>
        <div style={{ fontSize: 12, color: '#64748b', wordBreak: 'break-all' }}>{detail}</div>
      </div>
    </div>
  );
}
